import { NotifyErrorWrapper, NotifySuccess } from '@/helpers/core/notifications'
import {
  createSiteCurtailmentConfiguration,
  deleteSiteCurtailmentConfiguration,
  getSiteCurtailmentConfigurations,
  updateSiteCurtailmentConfiguration,
} from '@/services/eos/site-curtailment-configurations.service'
import type { ISiteCurtailmentConfigurationFilters } from '@/types/eos/filters'
import type { ISiteCurtailmentConfigurationUpsertForm } from '@/types/eos/forms'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'

export const useSiteCurtailmentConfigurations = () => {
  const client = useQueryClient()

  const getSiteCurtailmentConfigurationsQuery = (filters: ISiteCurtailmentConfigurationFilters) =>
    useQuery({
      queryKey: ['site-curtailment-configurations', filters],
      queryFn: () => getSiteCurtailmentConfigurations(filters),
    })

  const createSiteCurtailmentConfigurationMutation = useMutation({
    mutationFn: (form: ISiteCurtailmentConfigurationUpsertForm) => createSiteCurtailmentConfiguration(form),
    onError: NotifyErrorWrapper('Failed to create site curtailment configuration'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['site-curtailment-configurations'] })
      NotifySuccess(`Site curtailment configuration created successfully`)
    },
  })

  const updateSiteCurtailmentConfigurationMutation = useMutation({
    mutationFn: ({ id, form }: { id: number; form: ISiteCurtailmentConfigurationUpsertForm }) =>
      updateSiteCurtailmentConfiguration(id, form),
    onError: NotifyErrorWrapper('Failed to update site curtailment configuration'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['site-curtailment-configurations'] })
      NotifySuccess(`Site curtailment configuration updated`)
    },
  })

  const deleteSiteCurtailmentConfigurationMutation = useMutation({
    mutationFn: (id: number) => deleteSiteCurtailmentConfiguration(id),
    onError: NotifyErrorWrapper('Failed to delete site curtailment configuration'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['site-curtailment-configurations'] })
      NotifySuccess(`Site curtailment configuration deleted successfully`)
    },
  })

  return {
    getSiteCurtailmentConfigurationsQuery,
    createSiteCurtailmentConfigurationMutation,
    updateSiteCurtailmentConfigurationMutation,
    deleteSiteCurtailmentConfigurationMutation,
  }
}
